/**
 * Supabase browser client. Single shared instance.
 *
 * Storage / auto-refresh defaults follow `docs/frontend/035-frontend-app-shell-plan.md §8.3`:
 * the supabase-js client manages the session in localStorage and refreshes
 * the access token automatically. The shell never writes the session itself.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { env } from '../env';

function buildClient(): SupabaseClient {
  // When env is unset (typical for first-time dev or CI without secrets),
  // we still construct a client with placeholder values so the bundle runs.
  // Every authenticated call routes through the API client which surfaces
  // a typed envelope error instead of letting the placeholder leak.
  const url = env.supabaseUrl.length > 0 ? env.supabaseUrl : 'http://localhost:54321';
  const anonKey = env.supabaseAnonKey.length > 0 ? env.supabaseAnonKey : 'placeholder-anon-key';
  return createClient(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      // Force PKCE flow for all email/magic-link / recovery flows.
      // The /reset-password page (Auth UX task) consumes the recovery
      // link via `exchangeCodeForSession(code)`, which requires the
      // recovery email to deliver a `?code=...` query param — that's
      // the PKCE shape. supabase-js v2.x default is `implicit` (which
      // delivers `#access_token=...&type=recovery` hash fragments),
      // so we MUST set this explicitly. Without `flowType: 'pkce'`,
      // /reset-password gets no code and renders the invalid state
      // every time. Pinned in the auth shape tests.
      flowType: 'pkce',
    },
  });
}

export const supabase = buildClient();
